import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import {
  browserStorageAdapter,
  campaignStateForPersist,
  reduceGame,
  saveGameState,
  type GameAction,
  type GameState,
  type GameStateOverrides,
  type StorageAdapter,
} from "../../game/index.js";
import {
  bootGameControllerState,
  shouldPersistGameState,
} from "./game-session-boot.js";
import { createInitialGameState } from "../../game/reducer.js";
import { getArtifactCaptureQueueStats } from "../lib/artifact-capture.js";

export function useGameController(adapter: StorageAdapter = browserStorageAdapter()) {
  const boot = useMemo(() => {
    const search = typeof window !== "undefined" ? window.location.search : "";
    return bootGameControllerState(adapter, search);
  }, [adapter]);

  const [state, dispatchRaw] = useReducer(reduceGame, boot.state);
  const recoveredRef = useRef(boot.recovered);
  const skipPersist = useRef(true);

  const dispatch = useCallback((action: GameAction) => {
    dispatchRaw(action);
  }, []);

  useEffect(() => {
    if (skipPersist.current) {
      skipPersist.current = false;
      return;
    }
    // Session testSpeed must never leak economy/purchases/theme into campaign storage.
    if (!shouldPersistGameState(state)) return;
    saveGameState(adapter, campaignStateForPersist(state));
  }, [adapter, state]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.dataset.testSpeed = state.settings.testSpeed ? "1" : "0";
    document.documentElement.style.setProperty(
      "--desk-anim-ms",
      state.settings.testSpeed ? "40ms" : "900ms",
    );
    document.documentElement.style.setProperty(
      "--desk-count-ms",
      state.settings.testSpeed ? "80ms" : "1600ms",
    );
  }, [state.settings.testSpeed]);

  useEffect(() => {
    if (typeof window === "undefined" || !state.settings.testSpeed) {
      if (typeof window !== "undefined") {
        delete window.__DESK_TEST_FLUSH_SAVE__;
        delete window.__DESK_ARTIFACT_QUEUE_STATS__;
      }
      return;
    }
    // Explicit smoke/QA flush only — auto-persist stays off under testSpeed.
    window.__DESK_TEST_FLUSH_SAVE__ = () => {
      saveGameState(adapter, campaignStateForPersist(state));
    };
    window.__DESK_ARTIFACT_QUEUE_STATS__ = getArtifactCaptureQueueStats;
    return () => {
      delete window.__DESK_TEST_FLUSH_SAVE__;
      delete window.__DESK_ARTIFACT_QUEUE_STATS__;
    };
  }, [adapter, state]);

  useEffect(() => {
    if (typeof window === "undefined" || !state.settings.testSpeed) return;
    // Only exposed while URL/session testSpeed is active — never persists.
    window.__DESK_SEED_TEST_CASH__ = (amount: number) => {
      dispatch({ type: "SEED_TEST_CASH", amount });
    };
    return () => {
      delete window.__DESK_SEED_TEST_CASH__;
    };
  }, [state.settings.testSpeed, dispatch]);

  return {
    state,
    dispatch,
    recovered: recoveredRef.current,
    resetWithConfirm: () => {
      if (typeof window !== "undefined") {
        const ok = window.confirm(
          "Reset local shift progress, drafts, and portfolio? This cannot be undone.",
        );
        if (!ok) return;
      }
      dispatch({ type: "RESET_PROGRESS" });
    },
  };
}

export type GameController = ReturnType<typeof useGameController>;

declare global {
  interface Window {
    __DESK_SEED_TEST_CASH__?: (amount: number) => void;
    /** testSpeed-only explicit save flush for Playwright persistence proofs. */
    __DESK_TEST_FLUSH_SAVE__?: () => void;
    /** testSpeed-only artifact capture queue stats. */
    __DESK_ARTIFACT_QUEUE_STATS__?: typeof getArtifactCaptureQueueStats;
  }
}

/** Test helper — start from a known state without reading localStorage. */
export function createTestControllerState(
  overrides: GameStateOverrides = {},
): GameState {
  return createInitialGameState(overrides);
}
